
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import FuncionesContext, {
  getAtributo,
  useEsMobile,
} from "../../../context/FuncionesContext";
import { useRefDataStore } from "../../../../..";
import Nullifier from "../../../components/Nullifier";

const LABEL_CLASSNAME = "input_image_label";
const IMG_CLASSNAME = "input_image_preview";
const INPUT_CLASSNAME = "input_image_file_input";
const AVATAR_CLASSNAME = "input_image_avatar";
const PLACEHOLDER_CLASSNAME = "input_image_placeholder";
const ACTIONS_CLASSNAME = "input_image_actions";

const resolverSrcImagen = (valor) => {
  if (!valor) return "";
  return valor;
};

const normalizarDimension = (valor, fallback = "120px") => {
  if (valor === null || valor === undefined || valor === "") return fallback;
  if (typeof valor === "number") return `${valor}px`;

  const texto = valor.toString().trim();
  if (/^\d+$/.test(texto)) return `${texto}px`;
  return texto;
};

const tieneImagen = (valor) => {
  if (valor === null || valor === undefined) return false;
  if (typeof valor !== "string") return true;
  return valor.trim() !== "" && valor.trim().toLowerCase() !== "null";
};

const resolverValorPersistido = (result, campokey, cab) => {
  const responseData = result?.data;
  const registro = responseData?.registro;

  return (
    registro?.[campokey] ??
    registro?.[cab.update_campo] ??
    responseData?.[campokey] ??
    responseData?.valor ??
    null
  );
};

function InputImagen({
  data,
  cab,
  campokey,
  id_elemento,
  valor,
  updateid,
}) {
  const { superSubmit, requestErrorHandler } = useContext(FuncionesContext);
  const [storedValue, setStore] = useRefDataStore((s) => s[data._key + campokey]);
  const [storedUpdateId] = useRefDataStore(
    (s) => s[data._key + cab.update_id_alias],
  );

  const inputRef = useRef(null);
  const videoRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const esMobile = useEsMobile();

  const valorInicial = storedValue ?? valor ?? data[campokey] ?? "";
  const [imageValue, setImageValue] = useState(valorInicial);
  const [lastValue, setLastValue] = useState(valorInicial);
  const [updateId, setUpdateId] = useState(storedUpdateId ?? updateid);
  const [isUploading, setIsUploading] = useState(false);
  const [localPreview, setLocalPreview] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const optimisticValueRef = useRef(null);

  const nombre = useMemo(() => {
    return getAtributo("nombre", data, cab) ?? getAtributo("label", data, cab);
  }, [cab, data]);

  const disabled = useMemo(() => {
    const disabledAtributo = getAtributo("disabled", data, cab);
    return disabledAtributo === "s";
  }, [cab, data]);

  const className = useMemo(() => {
    return getAtributo("className", data, cab) ?? "";
  }, [cab, data]);

  const imageWidth = useMemo(() => {
    return normalizarDimension(getAtributo("width", data, cab));
  }, [cab, data]);

  const permiteNull = useMemo(() => {
    return getAtributo("permite_null", data, cab) === "s";
  }, [cab, data]);

  const imagePreview = useMemo(() => {
    return getAtributo("image_preview", data, cab) !== "n";
  }, [cab, data]);

  const mostrarBotones = useMemo(() => {
    return getAtributo("imagen_mostrar_botones", data, cab) === "s";
  }, [cab, data]);

  const aceptaImagenes = useMemo(() => {
    return getAtributo("acepta_imagenes", data, cab) === "s";
  }, [cab, data]);

  const aceptaCamara = useMemo(() => {
    return getAtributo("acepta_camara", data, cab) === "s";
  }, [cab, data]);

  const previewSrc = useMemo(() => {
    if (localPreview) return localPreview;
    return resolverSrcImagen(imageValue);
  }, [imageValue, localPreview]);
  const mostrarImagen = useMemo(() => {
    if (!tieneImagen(imageValue)) return false;
    return true;
  }, [imageValue]);

  const handleCancelar = () => {
    if (localPreview) {
      URL.revokeObjectURL(localPreview);
      setLocalPreview("");
    }
    setImageValue(lastValue);
  };

  useEffect(() => {
    const nuevoValor = storedValue ?? valor ?? data[campokey] ?? "";

    if (
      optimisticValueRef.current !== null &&
      nuevoValor !== optimisticValueRef.current
    ) {
      return;
    }

    if (optimisticValueRef.current === nuevoValor) {
      optimisticValueRef.current = null;
    }

    setImageValue(nuevoValor);
    setLastValue(nuevoValor);
  }, [storedValue, valor, data, campokey]);

  useEffect(() => {
    setUpdateId(storedUpdateId ?? updateid);
  }, [storedUpdateId, updateid]);

  useEffect(() => {
    return () => {
      if (localPreview) {
        URL.revokeObjectURL(localPreview);
      }
    };
  }, [localPreview]);

  useEffect(() => {
    if (cameraOpen && videoRef.current && cameraStreamRef.current) {
      videoRef.current.srcObject = cameraStreamRef.current;
    }
  }, [cameraOpen]);

  useEffect(() => {
    return () => {
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((track) => track.stop());
        cameraStreamRef.current = null;
      }
    };
  }, []);

  const persistirValor = async (valorASubir) => {
    const { id_a } = cab;
    optimisticValueRef.current = valorASubir;
    setStore({ [data._key + campokey]: valorASubir });

    return await superSubmit({
      valor: valorASubir,
      id_a,
      update_id: updateId,
      handleCancelar,
      cab,
      data,
    }).then((result) => {
      if (!updateId) {
        const nuevoUpdateId = result?.data?.registro?.id;
        if (nuevoUpdateId) {
          setStore({ [data._key + cab.update_id_alias]: nuevoUpdateId });
          setUpdateId(nuevoUpdateId);
        }
      }

      return result;
    });
  };

  const subirImagen = async (archivo, input = inputRef.current) => {
    if (!archivo || isUploading) return;

    setIsUploading(true);
    const previewTemporal = URL.createObjectURL(archivo);

    if (localPreview) {
      URL.revokeObjectURL(localPreview);
    }

    setLocalPreview(previewTemporal);
    setImageValue(previewTemporal);

    const cancelarSubida = () => {
      URL.revokeObjectURL(previewTemporal);
      setLocalPreview("");
      setImageValue(lastValue);
    };

    try {
      const result = await superSubmit({
        archivo,
        valor: imageValue,
        id_a: cab.id_a,
        update_id: updateId,
        handleCancelar: cancelarSubida,
        cab,
        data,
      });
      const valorPersistido = resolverValorPersistido(result, campokey, cab);

      if (valorPersistido) {
        optimisticValueRef.current = valorPersistido;
        setStore({ [data._key + campokey]: valorPersistido });
        URL.revokeObjectURL(previewTemporal);
        setLocalPreview("");
        setImageValue(valorPersistido);
        setLastValue(valorPersistido);
      }

      if (!updateId) {
        const nuevoUpdateId = result?.data?.registro?.id;
        if (nuevoUpdateId) {
          setStore({ [data._key + cab.update_id_alias]: nuevoUpdateId });
          setUpdateId(nuevoUpdateId);
        }
      }
    } catch (err) {
      cancelarSubida();
      if (
        !err?.config?.url ||
        !/\/(?:insertar|guardar)$/.test(err.config.url)
      ) {
        requestErrorHandler(err);
      }
    } finally {
      if (input) {
        input.removeAttribute("capture");
        input.value = "";
      }
      setIsUploading(false);
    }
  };

  const handleFileChange = async (event) => {
    const input = event.currentTarget;
    await subirImagen(input.files?.[0], input);
  };

  const cerrarCamara = () => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraOpen(false);
  };

  const abrirCamara = async () => {
    setCameraError("");

    if (!navigator.mediaDevices?.getUserMedia) {
      if (!inputRef.current) return;
      inputRef.current.setAttribute("capture", "environment");
      inputRef.current.click();
      return;
    }

    try {
      cameraStreamRef.current = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      setCameraOpen(true);
    } catch (error) {
      setCameraError("No se pudo acceder a la camara.");
    }
  };

  const capturarFoto = async () => {
    const video = videoRef.current;
    if (!video?.videoWidth || !video?.videoHeight) {
      setCameraError("La camara todavia no esta lista.");
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d");
    if (!context) {
      setCameraError("No se pudo capturar la imagen.");
      return;
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.92),
    );
    if (!blob) {
      setCameraError("No se pudo capturar la imagen.");
      return;
    }

    const archivo = new File([blob], `camara-${Date.now()}.jpg`, {
      type: "image/jpeg",
    });
    cerrarCamara();
    await subirImagen(archivo);
  };

  const handleNullificar = async () => {
    if (!permiteNull || disabled || isUploading) return;

    setIsUploading(true);
    try {
      if (localPreview) {
        URL.revokeObjectURL(localPreview);
        setLocalPreview("");
      }
      await persistirValor("null").then((result) => {
        setImageValue("");
        setLastValue("");
        return result;
      });
    } catch (err) {
      handleCancelar();
      if (
        !err?.config?.url ||
        !/\/(?:insertar|guardar)$/.test(err.config.url)
      ) {
        requestErrorHandler(err);
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div id={id_elemento}>
      {nombre ? (
        <div
          className={LABEL_CLASSNAME}
          style={{ fontWeight: "bold", marginBottom: "0.5rem" }}
        >
          {nombre}:
        </div>
      ) : null}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className={INPUT_CLASSNAME}
        style={{ display: "none" }}
        disabled={disabled || isUploading}
        onChange={handleFileChange}
      />

      <div
        className="input_image_preview_container"
      >
        <div
          role={mostrarBotones ? undefined : "button"}
          tabIndex={
            mostrarBotones ? undefined : disabled || isUploading ? -1 : 0
          }
          className={`${AVATAR_CLASSNAME} ${className || ""}`.trim()}
          style={{
            width: imageWidth,
            cursor:
              mostrarBotones || disabled || isUploading
                ? "default"
                : "pointer",
          }}
          onClick={
            mostrarBotones
              ? undefined
              : () => {
                  if (!disabled && !isUploading && inputRef.current) {
                    inputRef.current.click();
                  }
                }
          }
          onKeyDown={
            mostrarBotones
              ? undefined
              : (e) => {
                  if (e.key !== "Enter" && e.key !== " ") return;

                  e.preventDefault();
                  if (!disabled && !isUploading && inputRef.current) {
                    inputRef.current.click();
                  }
                }
          }
          aria-disabled={
            mostrarBotones ? undefined : disabled || isUploading
          }
        >
          {mostrarImagen ? (
            <img
              className={IMG_CLASSNAME}
              src={previewSrc}
              alt={nombre ?? cab.id_a}
              style={{
                width: imageWidth,
                maxWidth: "100%",
                display: "block",
              }}
            />
          ) : (
            <div
              className={PLACEHOLDER_CLASSNAME}
              style={{
                width: imageWidth,
                height: imageWidth,
                display: imagePreview || !mostrarImagen ? "flex" : "none",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {isUploading ? "Subiendo..." : "Sin Imagen"}
            </div>
          )}
        </div>

        {permiteNull && mostrarImagen ? (
          <Nullifier
            onClick={handleNullificar}
            className="input_image_delete_button"
            ariaLabel="Eliminar imagen"
            title="Eliminar imagen"
            disabled={disabled || isUploading}
          />
        ) : null}
      </div>

      {mostrarBotones ? (
        <div
          className={`${ACTIONS_CLASSNAME} input_image_action_buttons`}
        >
          <button
            type="button"
            className="btn-orixia input_image_action_button input_image_action_button_primary"
            disabled={disabled || isUploading || !aceptaImagenes}
            onClick={() => {
              if (!inputRef.current) return;
              inputRef.current.removeAttribute("capture");
              inputRef.current.click();
            }}
          >
            {mostrarImagen ? "Modificar imagen" : "Subir imagen"}
          </button>

          {esMobile ? (
            <button
              type="button"
              className="btn-orixia input_image_action_button input_image_action_button_camera"
              disabled={disabled || isUploading || !aceptaCamara}
              onClick={abrirCamara}
            >
              Usar Camara
            </button>
          ) : null}
        </div>
      ) : null}

      {cameraError && !cameraOpen ? (
        <div className="input_image_camera_error">{cameraError}</div>
      ) : null}

      {cameraOpen ? (
        <div className="input_image_camera_backdrop" role="dialog" aria-modal="true">
          <div className="input_image_camera_dialog">
            <video
              ref={videoRef}
              className="input_image_camera_video"
              autoPlay
              playsInline
              muted
            />
            {cameraError ? (
              <div className="input_image_camera_error">{cameraError}</div>
            ) : null}
            <div className="input_image_camera_actions">
              <button
                type="button"
                className="btn-orixia input_image_action_button input_image_action_button_primary"
                onClick={capturarFoto}
              >
                Tomar foto
              </button>
              <button
                type="button"
                className="btn-orixia input_image_action_button input_image_action_button_camera"
                onClick={cerrarCamara}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      ) : null}

    </div>
  );
}

export default InputImagen;
